import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface UserPrize {
  id: string;
  user_id: string;
  reward_id: string;
  prize_qr_token: string;
  level_earned: number;
  status: 'active' | 'redeemed' | 'expired';
  redeemed_at: string | null;
  created_at: string;
  reward?: {
    name: string;
    description: string | null;
    rarity: string;
  };
}

export const useUserPrizes = () => {
  const { user } = useAuth();
  const [prizes, setPrizes] = useState<UserPrize[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPrizes = useCallback(async () => {
    if (!user) {
      setPrizes([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      // First fetch user_prizes
      const { data: prizesData, error: prizesError } = await supabase
        .from('user_prizes')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (prizesError) throw prizesError;

      // Then fetch rewards for each prize
      if (prizesData && prizesData.length > 0) {
        const rewardIds = [...new Set(prizesData.map(p => p.reward_id))];
        const { data: rewardsData } = await supabase
          .from('rewards_pool')
          .select('id, name, description, rarity')
          .in('id', rewardIds);

        const rewardsMap = new Map(rewardsData?.map(r => [r.id, r]) || []);
        
        const prizesWithRewards = prizesData.map(prize => ({
          ...prize,
          status: prize.status as 'active' | 'redeemed' | 'expired',
          reward: rewardsMap.get(prize.reward_id) || undefined,
        }));

        setPrizes(prizesWithRewards);
      } else {
        setPrizes([]);
      }
    } catch (err) {
      console.error('Error fetching user prizes:', err);
      setError('Error al cargar los premios');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchPrizes();
  }, [fetchPrizes]);

  // Real-time subscription for new prizes
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel('user-prizes-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_prizes',
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          fetchPrizes();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, fetchPrizes]);

  const activePrizes = prizes.filter(p => p.status === 'active');
  const redeemedPrizes = prizes.filter(p => p.status === 'redeemed');

  return {
    prizes,
    activePrizes,
    redeemedPrizes,
    loading,
    error,
    refetch: fetchPrizes,
  };
};
